import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { 
  Box, 
  Typography, 
  Button, 
  Card, 
  CircularProgress, 
  Paper, 
  Dialog, 
  DialogTitle, 
  DialogContent, 
  DialogActions, 
  TextField,
  Snackbar
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { QuestionLayout } from '../../components/layout/QuestionLayout';
import { courses, flows, images, submitBugReport } from '../../services/api';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';

const StyledPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(4),
  borderRadius: theme.spacing(2),
  boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)',
  maxWidth: 800,
  margin: '0 auto',
  textAlign: 'center',
}));

const StyledButton = styled(Button)(({ theme }) => ({
  marginTop: theme.spacing(3),
  padding: theme.spacing(1.5, 4),
  borderRadius: 30,
  fontWeight: 600,
}));

const ImageCard = styled(Box)(({ theme }) => ({
  marginTop: theme.spacing(4),
  marginBottom: theme.spacing(4),
  maxWidth: 600,
  margin: '0 auto',
  backgroundColor: 'transparent',
}));

interface LocationState {
  completedFlowName?: string;
  completedFlowId?: number;
  courseId?: number;
}

const CourseContinue: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const state = location.state as LocationState;
  
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [completedFlowName, setCompletedFlowName] = useState<string>('');
  const [nextFlowName, setNextFlowName] = useState<string>('');
  const [nextFlowId, setNextFlowId] = useState<number | null>(null);
  const [courseId, setCourseId] = useState<number | null>(null);
  const [courseName, setCourseName] = useState<string>('');
  const [nextFlowImageUrl, setNextFlowImageUrl] = useState<string>('');
  
  // Bug report states
  const [isBugReportOpen, setIsBugReportOpen] = useState(false);
  const [bugFeedbackText, setBugFeedbackText] = useState('');
  const [isSubmittingBug, setIsSubmittingBug] = useState(false);
  const [bugReportSuccess, setBugReportSuccess] = useState(false);

  useEffect(() => {
    const fetchCourseData = async () => {
      try {
        setLoading(true);
        setError(null);

        // Get completed flow name and course ID from location state
        const flowName = state?.completedFlowName;
        const courseIdFromState = state?.courseId;

        console.log('CourseContinue received state:', state);

        if (!flowName || !courseIdFromState) {
          console.error('Missing required state:', { flowName, courseIdFromState });
          // If accessed directly without state, redirect to home
          navigate('/home');
          return;
        }

        setCompletedFlowName(flowName);
        setCourseId(courseIdFromState);

        // Get course details
        const courseResponse = await courses.get(courseIdFromState);
        if (!courseResponse.data || !courseResponse.data.name) {
          throw new Error('Invalid course data received');
        }
        setCourseName(courseResponse.data.name);

        // Get course flows
        const courseFlowsResponse = await courses.getFlows(courseIdFromState);
        const courseFlows = courseFlowsResponse.data;
        
        if (!Array.isArray(courseFlows) || courseFlows.length === 0) {
          throw new Error('No flows found in this course');
        }
        
        console.log('Course flows:', courseFlows);

        // Find the completed flow and determine the next flow
        let foundCompletedFlow = false;
        let nextFlow = null;

        for (let i = 0; i < courseFlows.length; i++) {
          const flow = courseFlows[i];
          console.log('Checking flow:', { 
            current: flow.flow?.name || flow.flow_details?.name,
            completed: flowName,
            match: (flow.flow?.name || flow.flow_details?.name) === flowName
          });
          
          if (foundCompletedFlow) {
            nextFlow = flow;
            break;
          }
          
          // Check both flow.flow.name and flow.flow_details.name
          const flowItemName = flow.flow?.name || flow.flow_details?.name;
          if (flowItemName === flowName) {
            foundCompletedFlow = true;
          }
        }

        if (nextFlow) {
          // Handle different response formats
          const nextFlowDetails = nextFlow.flow || nextFlow.flow_details;
          if (!nextFlowDetails) {
            throw new Error('Invalid next flow data structure');
          }
          
          console.log('Found next flow:', nextFlowDetails);
          setNextFlowName(nextFlowDetails.name);
          setNextFlowId(nextFlowDetails.id);

          // Get the first question of the next flow to display its image
          const flowQuestionsResponse = await flows.getQuestions(nextFlowDetails.id);
          const firstQuestion = flowQuestionsResponse.data[0];
          
          if (firstQuestion && firstQuestion.visual_component_url) {
            try {
              // Get signed URL for the image
              const signedUrl = await images.getSignedUrl(firstQuestion.visual_component_url);
              setNextFlowImageUrl(signedUrl);
            } catch (imageError) {
              console.error('Error fetching image:', imageError);
              // Continue without the image
            }
          }
        } else {
          // This was the last flow in the course
          setNextFlowName('');
          setNextFlowId(null);
        }

        setLoading(false);
      } catch (err) {
        console.error('Error fetching course data:', err);
        setError('Failed to load course information');
        setLoading(false);
      }
    };

    fetchCourseData();
  }, [state, navigate]);

  const handleContinue = () => {
    if (nextFlowName) {
      navigate(`/flows/${nextFlowName}`);
    } else {
      // If there's no next flow, go to the course completion page
      navigate('/course-completion', {
        state: {
          courseId: courseId,
          courseName: courseName
        }
      });
    }
  };

  const handleGoHome = () => {
    navigate('/home');
  };

  const handleBugReportSubmit = async () => {
    if (!bugFeedbackText.trim()) {
      return;
    }

    setIsSubmittingBug(true);
    try {
      // Prepare the current page context data
      const contextData = {
        page: 'CourseContinue',
        completedFlowName,
        nextFlowName,
        courseId,
        courseName
      };

      await submitBugReport({
        question_id: -1, // Use -1 to indicate this is not related to a specific question
        submitted_response: contextData,
        feedback_text: bugFeedbackText,
      });

      setBugReportSuccess(true);
      setIsBugReportOpen(false);
      setBugFeedbackText('');
    } catch (err) {
      console.error('Error submitting bug report:', err);
    } finally {
      setIsSubmittingBug(false);
    }
  };

  if (loading) {
    return (
      <QuestionLayout>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="80vh">
          <CircularProgress />
        </Box>
      </QuestionLayout>
    );
  }

  if (error) {
    return (
      <QuestionLayout>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="80vh">
          <StyledPaper>
            <Typography variant="h5" color="error" gutterBottom>
              {error}
            </Typography>
            <StyledButton 
              variant="contained" 
              color="primary" 
              onClick={handleGoHome}
            >
              Go to Home
            </StyledButton>
          </StyledPaper>
        </Box>
      </QuestionLayout>
    );
  }

  return (
    <QuestionLayout>
      <Box py={8} px={2}>
        <StyledPaper>
          <Typography variant="h4" gutterBottom sx={{ fontWeight: 600 }}>
            Congratulations!
          </Typography>
          
          <Typography variant="h6" gutterBottom>
            You've completed the "{completedFlowName}" section
          </Typography>
          
          <Typography variant="body1" color="text.secondary" sx={{ mt: 2, mb: 4 }}>
            {nextFlowName ? (
              <>You're making great progress in the {courseName} course!</>
            ) : (
              <>You've completed the entire {courseName} course!</>
            )}
          </Typography>

          {nextFlowImageUrl && (
            <ImageCard>
              <Box 
                component="img"
                src={nextFlowImageUrl}
                alt="Next section preview"
                sx={{
                  width: '100%',
                  maxHeight: 400,
                  objectFit: 'contain',
                  backgroundColor: 'transparent'
                }}
              />
            </ImageCard>
          )}

          {nextFlowName ? (
            <>
              <Typography variant="h6" gutterBottom>
                Continue your journey with "{nextFlowName}"
              </Typography>
              
              <StyledButton 
                variant="contained" 
                color="primary" 
                endIcon={<ArrowForwardIcon />}
                onClick={handleContinue}
              >
                Continue to Next Section
              </StyledButton>
            </>
          ) : (
            <>
              <Typography variant="h6" gutterBottom>
                You've reached the end of this course!
              </Typography>
              
              <StyledButton 
                variant="contained" 
                color="primary" 
                onClick={handleGoHome}
              >
                Return to Home
              </StyledButton>
            </>
          )}
        </StyledPaper>

        {/* Bug Report Button - Positioned in bottom right corner */}
        <Box 
          sx={{ 
            position: 'fixed',
            bottom: 24,
            right: 24,
            zIndex: 1000
          }}
        >
          <Button
            variant="outlined"
            color="error"
            size="small"
            onClick={() => setIsBugReportOpen(true)}
            sx={{
              borderRadius: 2,
              textTransform: 'none',
              fontSize: '0.875rem',
              backgroundColor: 'rgba(255, 255, 255, 0.9)',
              '&:hover': {
                backgroundColor: 'rgba(255, 255, 255, 1)',
              },
              boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
            }}
          >
            Submit Feedback
          </Button>
        </Box>

        {/* Bug Report Dialog */}
        <Dialog 
          open={isBugReportOpen} 
          onClose={() => !isSubmittingBug && setIsBugReportOpen(false)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>Submit Feedback</DialogTitle>
          <DialogContent>
            <Box sx={{ mt: 2 }}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Please describe the issue you're experiencing with this page:
              </Typography>
              <TextField
                fullWidth
                multiline
                rows={4}
                label="Issue Description"
                value={bugFeedbackText}
                onChange={(e) => setBugFeedbackText(e.target.value)}
                disabled={isSubmittingBug}
                placeholder="Describe what's wrong or confusing about this page..."
                sx={{ mb: 2 }}
              />
            </Box>
          </DialogContent>
          <DialogActions>
            <Button 
              onClick={() => setIsBugReportOpen(false)} 
              disabled={isSubmittingBug}
            >
              Cancel
            </Button>
            <Button 
              onClick={handleBugReportSubmit}
              variant="contained" 
              color="primary"
              disabled={!bugFeedbackText.trim() || isSubmittingBug}
            >
              {isSubmittingBug ? (
                <CircularProgress size={24} color="inherit" />
              ) : (
                'Submit Report'
              )}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Success Snackbar */}
        <Snackbar
          open={bugReportSuccess}
          autoHideDuration={6000}
          onClose={() => setBugReportSuccess(false)}
          message="Bug report submitted successfully"
        />
      </Box>
    </QuestionLayout>
  );
};

export default CourseContinue;
